import {
  FlashIcon,
  HideOutlineIcon,
  KeyIcon,
  MagicWandIcon,
  RevertOutlineIcon,
  RightOutlineIcon,
} from '@carboncopyco/icons';
import type { TableCraftFunction } from '@carboncopyco/types';
import { SubMenu, TextCaption, Tooltip } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useState } from 'react';
import { EditWithAI } from '../EditWithAI';
import { ColumnOptions } from './ColumnOptions';
import { ColumnOptionsItem } from './ColumnOptions/ColumnOptionsItem';
import FunctionsModal from './FunctionsModal';
import { useDropdownOnOpenChange } from './useOnDropdownOpenChange';

const getBgStyles = ({
  isSelected,
  isDropdownOpen,
  isDisabled,
}: {
  isSelected: boolean;
  isDropdownOpen: boolean;
  isDisabled;
}) => {
  if (isSelected) {
    return 'bg-white';
  }

  if (isDropdownOpen) {
    return 'bg-ds-natural-150';
  }

  if (isDisabled) {
    return 'bg-ds-natural-100';
  }

  return 'hover:bg-ds-natural-150 group-hover:bg-ds-natural-150';
};

type ThProps = {
  columnValues: unknown[];
  columnKey: string;
  label: string;
  isSelected: boolean;
  isModified: boolean;
  isDisabled: boolean;
  selectable: boolean;
  onAiResponse: (prompt: string) => Promise<void>;
  onColumnRevert: () => void;
  onSetColumnAsKey: () => void;
  onColumnHide?: () => void;
  onSelectedColumnChange: (key: string | undefined) => void;
  onDataFunctionsClick: ({
    columnKey,
    selectedFn,
    params,
  }: {
    columnKey: string;
    selectedFn: TableCraftFunction;
    params: Record<string, boolean | string>;
  }) => void;
  tableCraftFunctions?: TableCraftFunction[];
};

export const Th = ({
  columnValues,
  columnKey,
  label,
  isSelected,
  isModified,
  isDisabled,
  selectable,
  onAiResponse,
  onColumnRevert,
  onColumnHide,
  onSetColumnAsKey,
  onSelectedColumnChange,
  onDataFunctionsClick,
  tableCraftFunctions,
}: ThProps) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [showEditWithAiDialog, setShowEditWithAiDialog] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFn, setSelectedFn] = React.useState<TableCraftFunction>();
  const flags = useFlags();

  const { onOpenChange } = useDropdownOnOpenChange(onSelectedColumnChange);

  const isStringOrNumberValue = ['string', 'number'].includes(
    typeof columnValues[0]
  );

  const isColumnModified = isModified;

  const hasRevertColumnOption = ['string', 'number'].includes(
    typeof columnValues[0]
  );
  const hasColumnOptions =
    isStringOrNumberValue || hasRevertColumnOption || onColumnHide;

  const handleAIClick = () => {
    setShowEditWithAiDialog(true);
    onSelectedColumnChange(columnKey);
  };

  return (
    <>
      <th
        id={label}
        className={classNames(
          'border-t border-l border-solid border-ds-natural-150 group relative min-w-[60px]',
          { 'cursor-not-allowed': isDisabled },
          getBgStyles({
            isSelected,
            isDropdownOpen,
            isDisabled,
          })
        )}
      >
        <div
          className="flex items-center"
          ref={anchorRef}
        >
          {isSelected && (
            <div
              className={classNames(
                'absolute inset-0 bottom-0 border-x border-t border-solid border-ds-primary-300 pointer-events-none'
              )}
            />
          )}
          <button
            className={classNames('flex flex-1 items-center py-0 px-2 h-7', {
              'cursor-auto': !selectable,
              'cursor-not-allowed': isDisabled,
            })}
            type="button"
            onClick={
              selectable && !isDisabled
                ? () => {
                    onSelectedColumnChange(isSelected ? undefined : columnKey);
                  }
                : undefined
            }
            aria-label={label}
            disabled={isDisabled}
          >
            <TextCaption
              ellipsis
              color="text-ds-natural-600"
              label={label}
            />
          </button>

          <div
            className={classNames(
              'flex opacity-0 group-hover:opacity-100',
              getBgStyles({
                isSelected,
                isDropdownOpen,
                isDisabled,
              }),
              { 'absolute right-1.5': !isDisabled },
              { 'opacity-100 mr-1.5': isDisabled },
              { 'opacity-100': isDropdownOpen }
            )}
          >
            {isDisabled ? (
              <div className="text-ds-natural-400">
                <KeyIcon size={16} />
              </div>
            ) : (
              <>
                {hasColumnOptions && (
                  <ColumnOptions
                    isOpen={isDropdownOpen}
                    onOpenChange={(isOpen) => {
                      setIsDropdownOpen(isOpen);
                      onOpenChange(isOpen || showEditWithAiDialog, columnKey);
                    }}
                  >
                    {isStringOrNumberValue && (
                      <>
                        <ColumnOptionsItem
                          label="Generate values with AI"
                          iconLeft={<MagicWandIcon size={20} />}
                          onClick={handleAIClick}
                        />
                        <ColumnOptionsItem
                          label="Set column as a key"
                          iconLeft={<KeyIcon size={20} />}
                          onClick={onSetColumnAsKey}
                        />
                        {flags.editorObjectEditingDataFunctions && (
                          <SubMenu
                            triggerProps={{
                              label: 'Data functions',
                              iconRight: <RightOutlineIcon size={20} />,
                              iconLeft: <FlashIcon size={20} />,
                              disabled: !tableCraftFunctions,
                            }}
                          >
                            {tableCraftFunctions?.map((func) => (
                              <ColumnOptionsItem
                                key={func.name}
                                label={func.display_name}
                                onClick={() => {
                                  setSelectedFn(func);
                                }}
                                tooltipTitle={func.tooltipTitle}
                                tooltipDescription={func.help}
                              />
                            ))}
                          </SubMenu>
                        )}
                      </>
                    )}
                    {hasRevertColumnOption && (
                      <ColumnOptionsItem
                        label="Revert column edits"
                        iconLeft={<RevertOutlineIcon size={20} />}
                        onClick={onColumnRevert}
                        disabled={!isColumnModified}
                      />
                    )}
                    {onColumnHide && (
                      <ColumnOptionsItem
                        label="Hide column"
                        iconLeft={<HideOutlineIcon size={20} />}
                        onClick={onColumnHide}
                      />
                    )}
                  </ColumnOptions>
                )}
              </>
            )}
          </div>
        </div>
      </th>

      <Tooltip
        visible={showEditWithAiDialog}
        interactive
        zIndex={10}
        placement="right-start"
        appendTo={() => document.body}
        anchorRef={anchorRef}
        background="light"
        onClickOutside={() => {
          setShowEditWithAiDialog(false);
          onSelectedColumnChange(undefined);
        }}
        shadow="none"
      >
        {showEditWithAiDialog && (
          <EditWithAI
            onResponse={onAiResponse}
            onClose={() => {
              setShowEditWithAiDialog(false);
              onSelectedColumnChange(undefined);
            }}
          />
        )}
      </Tooltip>

      {selectedFn && (
        <FunctionsModal
          fn={selectedFn}
          onClose={() => setSelectedFn(undefined)}
          onSubmit={(params) => {
            onDataFunctionsClick({
              columnKey,
              selectedFn,
              params,
            });
            setSelectedFn(undefined);
          }}
        />
      )}
    </>
  );
};
